// Grammar Units 5 Data - Unit 5 (Conditional Sentences)
// Based on Ch05.jsx - Complete Conditional Sentences Learning
// Organized for Supabase migration and systematic data management

export const grammarUnits5Data = [
  {
    id: 5,
    title: { traditional: '假設語氣完整學習', simplified: '假设语气完整学习', english: 'Complete Conditional Sentences Learning' },
    subtitle: { traditional: '掌握假設語氣的各種用法', simplified: '掌握假设语气的各种用法', english: 'Master various uses of conditional sentences' },
    description: { traditional: '深入學習英文假設語氣，包括與現在、過去、未來事實相反的假設句和其他常見句型。', simplified: '深入学习英文假设语气，包括与现在、过去、未来事实相反的假设句和其他常见句型。', english: 'In-depth learning of English conditional sentences including contrary-to-fact conditionals and other common patterns.' },
    icon: '🎭',
    color: 'from-pink-500 to-rose-600',
    status: 'notStarted',
    difficulty: 'advanced',
    estimatedHours: 4,
    content: {
      overview: {
        title: "假設語氣 (Conditional Sentences)",
        timeline: [
          { period: "past", label: "與過去事實相反", color: "#e3f2fd" },
          { period: "now", label: "與現在事實相反", color: "#f3e5f5" },
          { period: "future", label: "與未來事實相反", color: "#e8f5e8" }
        ],
        categories: [
          "1. if 引導的假設語氣",
          "2. 「似乎、好像、希望、要不是」等具有假設語氣的句型用法",
          "3. 名詞子句的假設用法"
        ]
      },
      presentContrary: {
        title: "1. 與現在事實相反的假設句",
        description: "這種假設表示「現在幾乎不可能或完全不可能發生的事實或願望」。",
        patterns: [
          {
            id: "1-1",
            title: "if + 過去式助動詞 + V.",
            structure: "If + S. + 過去式助動詞 + V.",
            mainClause: "S. + could/might/should/would + V.",
            purpose: "因為意思上的需求，if 子句可用「if + 過去式助動詞 + V.」來表示。",
            examples: [
              {
                english: "If I could fly, I would fly to you right now.",
                chinese: "如果我可以飛的話，我現在會立刻飛到你身邊。"
              }
            ]
          },
          {
            id: "1-2",
            title: "if 子句不論人稱，be 動詞一律用 were",
            structure: "If + S. + were",
            mainClause: "與現在事實相反的假設法中的 be 動詞，不論人稱一律要用 were。",
            examples: [
              {
                english: "If Andy were not so poor, he would donate more money to the orphanage.",
                chinese: "如果安迪不是這麼窮的話，他會捐更多錢給孤兒院。"
              }
            ]
          },
          {
            id: "1-3",
            title: "if 子句的倒裝 (if 的省略)",
            structure: "原 if 子句 → 倒裝 / If + S. + were..., → Were + S....,",
            mainClause: "S. + could/would/might/should + V.",
            note: "were 或助動詞的倒裝",
            examples: [
              {
                english: "If Dan were more patient, he could sell his house for a better price.",
                chinese: "如果丹有多一點耐性的話，他就可以把房子賣到更好的價錢。",
                inverted: "Were Dan more patient, he could sell his house for a better price."
              }
            ]
          }
        ]
      },
      pastContrary: {
        title: "2. 與過去事實相反的假設句",
        description: "這種假設表示「過去並未發生的事情、或是過去未能實現的願望」。",
        patterns: [
          {
            id: "2-1",
            title: "過去的假設影響到現在的結果",
            structure: "If + S. + had + p.p., (then) S. + could/might/should/would + 原形動詞",
            examples: [
              {
                english: "If Bill had left college, he wouldn't have the great job he has now.",
                chinese: "如果比爾當初沒唸完大學，現在他就不會擁有這麼好的一份工作。"
              }
            ]
          },
          {
            id: "2-2",
            title: "if 子句的倒裝 (if 的省略)",
            structure: "If + S. + had + p.p., 倒裝 → Had + S. + p.p.,",
            mainClause: "S. + could/would/might/should + V./have + p.p.",
            examples: [
              {
                english: "If Ryan had left earlier, he would have been on time.",
                chinese: "如果萊恩早點離開的話，他就能準時到達了。",
                inverted: "Had Ryan left earlier, he would have been on time."
              }
            ]
          }
        ]
      },
      futureContrary: {
        title: "3. 與未來事實相反的假設句",
        description: "這種假設表示「未來並不可能發生，或發生的機率非常低」。",
        patterns: [
          {
            id: "3-1",
            title: "對未來某事抱持強烈懷疑時，亦可用假設法未來式",
            structure: "If + S. + should + V., (then) S. + 過去式助動詞 + V. / S. + 助動詞 + V. / 祈使句",
            meaning: "「萬一......的話」的意思",
            examples: [
              {
                english: "If you should get lost in the woods, what could you do to find your way out?",
                chinese: "萬一你在森林裡迷路的話，你可以怎麼做才能找到路？"
              }
            ]
          },
          {
            id: "3-2",
            title: "if 子句的倒裝 (if 的省略)",
            structure: "If + S. + should + V...., 倒裝 → Should + S. + V....,",
            mainClause: "S. + could/would/might/should + V.",
            examples: [
              {
                english: "If any parts should be missing, you should contact the manufacturer.",
                chinese: "萬一有任何零件不見的話，你應該連絡廠商。",
                inverted: "Should any parts be missing, you should contact the manufacturer."
              }
            ]
          }
        ]
      },
      otherPatterns: {
        title: "4. 其他常見的假設句法",
        patterns: [
          {
            id: "4-1",
            title: "as if (= as though) (似乎；好像)",
            structures: [
              {
                pattern: "S. + V. + as if + S. + were/過去式動詞",
                meaning: "表與現在事實相反"
              },
              {
                pattern: "S. + V. + as if + S. + had + p.p.",
                meaning: "表與過去事實相反"
              }
            ],
            examples: [
              {
                english: "He acts as if he were two years old.",
                chinese: "他的行為舉止好像兩歲小孩。",
                note: "其實他不是兩歲小孩"
              }
            ]
          },
          {
            id: "4-2",
            title: "if only 的句型用法",
            description: "「只要......就能......」希望某事在現在或未來可以成真 (= I hope)",
            structures: [
              "If only + 現在簡單式",
              "If only + were/過去簡單式 → I wish + were/過去簡單式",
              "If only + would + V.",
              "If only + 過去完成式"
            ]
          },
          {
            id: "4-3",
            title: "wish 的假設用法",
            description: "wish 用於表達不太可能實現的願望，之後所接的子句須用假設法來表示。",
            patterns: [
              {
                condition: "表現在未來不太可能實現的願望",
                structure: "S. + wish + (that) + S. + were/過去式動詞/過去式助動詞 + V."
              },
              {
                condition: "對過去未能實現的願望感到惋惜",
                structure: "S. + wish + (that) + S. + had + p.p. (過去完成式)/過去式助動詞 + have + p.p."
              }
            ]
          }
        ]
      },
      practiceQuestions: [
        {
          question: "Choose the correct conditional form: 'If I ___ rich, I would travel the world.'",
          options: ["am", "were", "will be", "had been"],
          correct: 1,
          explanation: "Use 'were' for present contrary-to-fact conditionals."
        },
        {
          question: "Complete: 'If she ___ studied harder, she would have passed the exam.'",
          options: ["has", "had", "would have", "will have"],
          correct: 1,
          explanation: "Use 'had + past participle' for past contrary-to-fact conditionals."
        },
        {
          question: "Which sentence shows future possibility? 'If it ___ rain tomorrow, we will stay home.'",
          options: ["should", "would", "had", "were"],
          correct: 0,
          explanation: "'Should' expresses future possibility in conditional sentences."
        },
        {
          question: "Choose the correct form: 'I wish I ___ speak French.'",
          options: ["can", "could", "will", "would"],
          correct: 1,
          explanation: "Use 'could' after 'wish' for present contrary-to-fact wishes."
        },
        {
          question: "Complete: 'He acts as if he ___ the boss.'",
          options: ["is", "was", "were", "had been"],
          correct: 2,
          explanation: "Use 'were' after 'as if' for contrary-to-fact situations."
        }
      ],
      memoryTips: {
        title: "記憶技巧 Memory Tips",
        tips: [
          {
            category: "時間軸記憶法",
            content: "用時間軸視覺化：過去(had+p.p.) → 現在(were/did) → 未來(should+V)"
          },
          {
            category: "倒裝記憶口訣",
            content: "Had/Were/Should 開頭，if 就省掉"
          },
          {
            category: "主句助動詞",
            content: "Could(能力) Would(意願) Might(可能) Should(應該)"
          },
          {
            category: "Be動詞規則",
            content: "假設語氣中，be動詞一律用were，不論人稱"
          }
        ]
      }
    }
  }
];

// Export individual units for specific access
export const getUnitById = (unitId) => {
  return grammarUnits5Data.find(unit => unit.id === unitId);
};

export const getUnitsByDifficulty = (difficulty) => {
  return grammarUnits5Data.filter(unit => unit.difficulty === difficulty);
};

export const getCompletedUnits = () => {
  return grammarUnits5Data.filter(unit => unit.status === 'completed');
};

// Default export
export default grammarUnits5Data;
